import { DndContext, DragEndEvent, DragOverEvent, KeyboardSensor, PointerSensor, closestCorners, useDroppable, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { faCalendarDays, faClock, faComment, faStar } from '@fortawesome/pro-light-svg-icons'
import { CustomerProjectModelType, projectTypes, projectTypesInversed, ProjectTypesInversedKeys, ProjectTypesKeys } from '@teinor/erp/types/company/customer/customerProject'
import { CustomerProjectStatusModelType } from '@teinor/erp/types/company/customer/customerProjectStatus'
import { useCallback, useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import { ConnectedProps, connect } from 'react-redux'
import { useParams } from 'react-router-dom'
import { WhiteBox } from '../../../../../../../baseComponents/AppLayout/WhiteBox'
import LoadingSpinner from '../../../../../../../baseComponents/LoaderDecider/LoadingSpinner'
import TwinIcon from '../../../../../../../baseComponents/TwinIcon'
import { Where } from '../../../../../../../baseComponents/TwinTable/types'
import ModalEditCustomerProject from '../../../../../../../specificComponents/Customers/ModalEditCustomerProject'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { displayDate, displayFormatedHourMinsFromSecs } from '../../../../../../../utils/globals/date'
import withLoading from '../../../../../../../utils/hoc/withLoading'
import useEditDeleteModal from '../../../../../../../utils/hooks/useEditDeleteModal'
import { AllReduxPayloads } from '../../../../../../../utils/reducers'
import { CustomerProjectTypePayload } from '../../../../../../../utils/reducers/customers/customerProjectTypes'
import { CustomerProjectFlowUpdatedDataForApi, CustomerProjectStatusesDictForFlow, CustomerProjectStatusesTimeByCols, TotalsResultBagOfHoursProject } from './types'
import { CustomerProjectRenderImageTooltip } from '../../../../../../../specificComponents/Customers/Subcomponents'
import { listenSocket, unlistenSocket } from '../../../../../../../utils/globals/socket'
import { EmployeesPayload } from '../../../../../../../utils/reducers/company/employees'
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons'
import DisplayPrice from '../../../../../../../baseComponents/Displays/DisplayPrice'
import { getISOCurrencyMain, getUserPermissions } from '../../../../../../../utils/reducers/getters'
import CustomerProjectFlowHeader from './CustomerProjectFlowHeader'
import './customerProjectFlow.sass'
import CustomerProjectFlowSubHeader from './CustomerProjectFlowSubHeader'
import { CustomerProjectsInner } from '../../CustomerProjects'
import { getPercentatge } from '../../../../../../../utils/globals/numbers'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { CustomerOpportunitiesInner } from '../../CustomerOpportunities'
import { ButtonChangeToStatusView } from '../../../../../../../specificComponents/ButtonChangeStatusView'
import { TwinDictionary } from '../../../../../../../utils/globals/dictionary'

interface CustomerProjectFlowProps extends CustomerProjectFlowLogicProps {
    projectTypeStatus: ProjectTypesKeys
}

const CustomerProjectFlow: React.FC<CustomerProjectFlowProps> = ({ customerProjectTypes, projectTypeStatus = 1, ...logic }) => {
    const renderThis: JSX.Element[] = []
    const { projects, getProjects, handleOnDragEnd, onHandleDragOver, sensors, myProjectTypeId, searchValue, setSearchValue, openModal, setOpenModal, handleModalEditOpened, setSelectedEmployeeId, selectedEmployeeId, setViewFromProjectsFlowHeader, viewFromProjectsFlowHeader, timeByColumns } = useCustomerProjectFlowLogic({ customerProjectTypes, projectTypeStatus, ...logic })
    const projectType = customerProjectTypes?.[myProjectTypeId]
    if (!projectType) {
        return null
    }
    if (viewFromProjectsFlowHeader === 'default') {
        if (projectTypeStatus === projectTypesInversed['opportunity']) {
            return <CustomerOpportunitiesInner userPermissions={getUserPermissions('customer.projects.main') || { n: 1, permission: 'read' }} RightHeader={<ButtonChangeToStatusView onClick={() => setViewFromProjectsFlowHeader('customer_projects_status_view')} className='mr-10' />} />
        }
        return <CustomerProjectsInner userPermissions={getUserPermissions('customer.projects.main') || { n: 1, permission: 'read' }} RightHeader={<ButtonChangeToStatusView onClick={() => setViewFromProjectsFlowHeader('customer_projects_status_view')} className='mr-10' />} />
    }

    let statuses: CustomerProjectStatusModelType[] = []
    if (projectType) {
        statuses = projectType.CustomerProjectStatusesByType[projectTypes[projectTypeStatus as ProjectTypesKeys] as ProjectTypesInversedKeys] || []
        for (const element of statuses) {
            if (element.listing) {
                renderThis.push(
                    <DroppableStatusColumn id={String(element.id)} status={element} projects={projects?.[element.id] || []} key={element.id} handleModalEditOpened={handleModalEditOpened} timeByColumns={timeByColumns} />
                )
            }
        }
    }
    return (
        <div className='py-36 px-50 flex flex-auto h-1 customer_project_flow'>
            <WhiteBox className='flex flex-auto flex-col py-40 px-60 w-1'>
                <CustomerProjectFlowHeader setView={setViewFromProjectsFlowHeader} customerProjectTypeId={myProjectTypeId} getData={getProjects} customerProjectTypes={customerProjectTypes} ProjectTypeId={myProjectTypeId} projectTypeStatus={projectTypeStatus} {...{ ...logic }} />
                <CustomerProjectFlowSubHeader statuses={statuses} setSearchValue={setSearchValue} searchValue={searchValue} {...{ selectedEmployeeId, setSelectedEmployeeId }} />
                {!projects ?
                    <LoadingSpinner /> :
                    <div className='grid gap-10 flex-auto mt-30 overflow-x-auto' style={{ gridTemplateColumns: `repeat(${renderThis.length}, minmax(260px, 290px))` }}>
                        <DndContext
                            sensors={sensors}
                            onDragEnd={handleOnDragEnd}
                            onDragOver={onHandleDragOver}
                            collisionDetection={closestCorners}
                        >
                            {renderThis}
                        </DndContext>
                    </div>
                }
            </WhiteBox>
            {openModal?.type === 'edit' ? <ModalEditCustomerProject setOpened={() => { setOpenModal(null); getProjects() }} onSubmit={() => { }} allRowData={openModal.allRowData} /> : null}
        </div>
    )
}

interface CustomerProjectFlowLogicProps extends ReduxCustomerProjectTypes {
    projectTypeStatus: ProjectTypesKeys
}

const useCustomerProjectFlowLogic = ({ customerProjectTypes, projectTypeStatus }: CustomerProjectFlowLogicProps) => {
    const [searchValue, setSearchValue] = useState('')
    const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null)
    const [projects, setProjects] = useState<CustomerProjectStatusesDictForFlow | null>(null)
    const { openModal, setOpenModal } = useEditDeleteModal()
    const [viewFromProjectsFlowHeader, setViewFromProjectsFlowHeader] = useState<'default' | 'customer_projects_status_view'>('customer_projects_status_view')
    const [timeByColumns, setTimeByColumns] = useState <CustomerProjectStatusesTimeByCols>({haveBagOfHours: false, timeByColumns: {}})

    const { id } = useParams()
    const myProjectTypeId = parseInt(String(id))

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { delay: 200, tolerance: 5 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    )

    const onHandleDragOver = useCallback((event: DragOverEvent) => {
        const { active, over } = event
        const activeId = active.id
        const overId = parseInt(String(over?.id) || '')
        const activeContainer = parseInt(active?.data?.current?.sortable?.containerId)
        let overContainer = 0
        if (over?.data?.current && 'sortable' in over?.data?.current) {
            overContainer = parseInt(over?.data?.current?.sortable?.containerId)
        } else {
            overContainer = parseInt(over?.data?.current?.columnId)
        }

        if (!activeContainer || !overContainer || activeContainer === overContainer) {
            return
        }
        setProjects((old) => {
            if (!old) {
                return null
            }
            const copy = JSON.parse(JSON.stringify(old))

            const activeItems = copy[activeContainer]
            const overItems = copy[overContainer]

            if (!copy[overContainer]) {
                copy[overContainer] = []
            }

            const activeIndex = activeItems.findIndex(
                (item: any) => item.id === activeId
            )
            const overIndex = overItems?.findIndex((item: any) => item.id === overId);
            let newIndex: number;
            if (copy && overId in copy) {
                newIndex = (overItems?.length || 0) + 1
            } else {
                const isBelowOverItem =
                    over &&
                    active.rect.current.translated &&
                    active.rect.current.translated.top > over.rect.top + over.rect.height;
                const modifier = isBelowOverItem ? 1 : 0;
                newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
            }
            return {
                ...copy,
                [activeContainer]: copy[activeContainer].filter(
                    (item: any) => item.id !== active.id
                ),
                [overContainer]: [
                    ...copy[overContainer].slice(0, newIndex),
                    copy[activeContainer][activeIndex],
                    ...copy[overContainer].slice(newIndex, copy[overContainer].length)
                ]
            }
        })

    }, [setProjects])

    const handleOnDragEnd = useCallback(async (event: DragEndEvent) => {
        const { active, over } = event
        const activeId = active.id
        const overId = parseInt(String(over?.id) || '')
        const activeContainer = parseInt(active?.data?.current?.sortable?.containerId)
        let overContainer = parseInt(over?.data?.current?.sortable?.containerId)
        let customerProjectWithStatus: CustomerProjectFlowUpdatedDataForApi | null = null

        if (!activeContainer || !overContainer || activeContainer !== overContainer) {
            return
        }
        flushSync(async () => {
            await setProjects((old) => {
                if (!old) {
                    return null
                }
                let copy = JSON.parse(JSON.stringify(old))
                const activeIndex = copy[activeContainer].findIndex((item: any) => {
                    return item.id === activeId
                })
                const myOverContainer = copy[overContainer]
                const overIndex = myOverContainer.findIndex(
                    (item: any) => item.id === overId
                )
                if (!myOverContainer) {
                    copy[overContainer] = []
                }
                if (activeIndex !== overIndex) {
                    copy = { ...copy, [overContainer]: arrayMove(myOverContainer, activeIndex, overIndex) }
                }
                let order = 0
                const originalContainer = parseInt(active?.data?.current?.originalContainer || '0')
                if (originalContainer && overContainer !== originalContainer) {
                    for (const key in copy[originalContainer]) {
                        copy[originalContainer][key].order = order + 1
                        order++
                    }
                }
                order = 0
                for (const key in myOverContainer) {
                    copy[overContainer][key].order = order + 1
                    if ((active.id === copy[overContainer][key].id)) {
                        customerProjectWithStatus = {
                            id: copy[overContainer][key].id,
                            name: copy[overContainer][key].name,
                            order: copy[overContainer][key].order,
                            CurrentCProjectStatusId: overContainer
                        }
                    }
                    copy[overContainer][key].originalContainer = overContainer
                    order++
                }
                return copy
            })
            if (customerProjectWithStatus) {
                await twinFetchPostJSON('/api/app/customer/customerProject/updateCustomerProjectCurrentStatus', { CurrentCProjectStatusId: overContainer, order: customerProjectWithStatus.order, id: activeId })
            }
        })
    }, [setProjects])

    const getProjects = useCallback(async () => {
        const projectType: any = customerProjectTypes?.[myProjectTypeId]
        const myProjectTypeStatusForUse: TwinDictionary = {
            0: 'CustomerPTForOpportunityId',
            1: 'CustomerPTForProjectId'
        }
        const myProjectTypeUse = myProjectTypeStatusForUse[projectTypeStatus]
        const myProjectSubTypeWhere = projectType?.[myProjectTypeUse]
        let where: Where = {
            'CustomerProject.CustomerProjectTypeId': myProjectSubTypeWhere ? myProjectTypeId : undefined,
            'CustomerProject.projectType': projectTypeStatus
        }
        if (searchValue && searchValue.length) {
            where['$or'] = {
                'Customer.name': { $iLike: '%' + searchValue + '%' },
                'CustomerProject.name': { $iLike: '%' + searchValue + '%' },
            }
        }
        if (selectedEmployeeId) {
            where['Employees->CustomerProjectEmployee.EmployeeId'] = selectedEmployeeId
        }
        const myProjectStatusDict: CustomerProjectStatusesDictForFlow = {}
        let statusesWhere: number[] = []
        if (projectType) {
            const statuses = projectType.CustomerProjectStatusesByType[projectTypes[projectTypeStatus as ProjectTypesKeys] as ProjectTypesInversedKeys] || []
            for (const element of statuses) {
                if (element.listing) {
                    statusesWhere.push(element.id)
                    myProjectStatusDict[element.id] = []
                }
            }
        }
        where['CustomerProject.CurrentCProjectStatusId'] = { '$or': statusesWhere }
        if (statusesWhere.length) {
            const result = await twinFetchPostJSON('/api/app/customer/customerProject/getAllCustomerProjectsComplete', {
                where,
                order: [['order', 'ASC']]
            })
            if (result) {
                let haveBagOfHours = false
                let timeByColumns: TwinDictionary = {}
                for (const element of result) {
                    if (element.TaskBagsOfHours?.length) {
                        haveBagOfHours = true
                    }
                    if (!timeByColumns[element.CurrentCProjectStatusId]) {
                        timeByColumns[element.CurrentCProjectStatusId] = {
                            timeLimit: 0,
                            remainingTime: 0,
                            consumedTime: 0,
                            consumedPercentatge: 0
                        }
                    }
                    const myProjectTime = {
                        timeLimit: 0,
                        remainingTime: 0,
                        consumedTime: 0,
                        consumedPercentatge: 0
                    }
                    if (element.TaskBagsOfHours?.length) {
                        for (const bagOfHours of element.TaskBagsOfHours) {
                            if (bagOfHours.active && parseInt(String(bagOfHours?.timeLimit)) !== 0) {
                                timeByColumns[element.CurrentCProjectStatusId].timeLimit += parseInt(String(bagOfHours.timeLimit))
                                timeByColumns[element.CurrentCProjectStatusId].remainingTime += parseInt(String(bagOfHours.remainingTime))
                                timeByColumns[element.CurrentCProjectStatusId].consumedTime += parseInt(String(bagOfHours.consumedTime))
                                myProjectTime.timeLimit += parseInt(String(bagOfHours.timeLimit))
                                myProjectTime.remainingTime += parseInt(String(bagOfHours.remainingTime))
                                myProjectTime.consumedTime += parseInt(String(bagOfHours.consumedTime))
                            }
                        }
                        timeByColumns[element.CurrentCProjectStatusId].consumedPercentatge = getPercentatge(timeByColumns[element.CurrentCProjectStatusId].remainingTime || 0, timeByColumns[element.CurrentCProjectStatusId].timeLimit || 0)
                        myProjectTime.consumedPercentatge = getPercentatge(myProjectTime.remainingTime || 0, myProjectTime.timeLimit || 0)
                    }
                    element.myProjectTime = myProjectTime
                    myProjectStatusDict[element.CurrentCProjectStatusId].push({
                        ...element,
                        order: element.order,
                        originalContainer: element.CurrentCProjectStatusId
                    })
                }
                setTimeByColumns({ haveBagOfHours, timeByColumns })
                setProjects(myProjectStatusDict)
            }
        } else {
            setTimeByColumns((old) => { return { haveBagOfHours: old.haveBagOfHours, timeByColumns: {} } })
            setProjects({})
        }
    }, [setProjects, myProjectTypeId, searchValue, customerProjectTypes, selectedEmployeeId, projectTypeStatus])

    const handleModalEditOpened = useCallback((project: CustomerProjectModelType) => {
        setOpenModal({
            type: 'edit',
            allRowData: { ...project }
        })
    }, [setOpenModal])

    useEffect(() => {
        listenSocket('projectStatusFlow-' + myProjectTypeId, getProjects, true)
        return () => {
            unlistenSocket('projectStatusFlow-' + myProjectTypeId)
        }
    }, [getProjects, myProjectTypeId])

    useEffect(() => {
        getProjects()
    }, [getProjects])

    return { sensors, projects, getProjects, handleOnDragEnd, onHandleDragOver, searchValue, setSearchValue, myProjectTypeId, openModal, setOpenModal, handleModalEditOpened, setSelectedEmployeeId, selectedEmployeeId, viewFromProjectsFlowHeader, setViewFromProjectsFlowHeader, timeByColumns }
}

interface DroppableStatusColumnProps {
    id: string
    status: CustomerProjectStatusModelType
    projects?: any
    handleModalEditOpened: (project: CustomerProjectModelType) => void
    timeByColumns: CustomerProjectStatusesTimeByCols
}

const DroppableStatusColumn: React.FC<DroppableStatusColumnProps> = ({ id, status, projects, handleModalEditOpened, timeByColumns }) => {
    const { setNodeRef } = useDroppable({
        id,
        data: {
            columnId: id
        }
    })
    let totalPercentageValue= 0
    const renderThis: JSX.Element[] = []
    const renderProjectProgressBar: JSX.Element[] = []
    for (const key in projects) {
        const item = projects[key]
        totalPercentageValue+= parseFloat(item?.percentageValue|| '0')
        renderThis.push(
            <DraggableProject project={item} id={item.id} index={item.id} key={item.id} originalContainer={item.originalContainer} handleModalEditOpened={handleModalEditOpened} totalsProject={item.myProjectTime} />
        )
    }
    const statusColor = status.color
    if (timeByColumns.haveBagOfHours) {
        const timeCol = timeByColumns.timeByColumns[status.id]
        if (timeCol?.timeLimit) {
            renderProjectProgressBar.push(
                <ProjectBagOfHoursProgressBar totalsTime={timeCol || {}} />
            )
        } else {
            renderProjectProgressBar.push(<div className='h-32'></div>)
        }
    }

    return (
        <SortableContext items={projects} strategy={rectSortingStrategy} id={id}>
            <div className='flex flex-col mr-15'>
                <div className='py-12 px-20 bg-gray-F7 border-t-4' style={{ borderColor: statusColor }}>
                    <div className='flex items-center gap-2'>
                        <h2 className='text-gray-51 text-18'>{status.name}</h2>
                        <div className='flex items-center justify-center h-20 min-w-20 px-5 ml-auto bg-gray-DD rounded-full light12 totals_info_circle'>{renderThis.length}</div>
                    </div>
                    <DisplayPrice value={totalPercentageValue} CurrencyId={getISOCurrencyMain()} with_vat={false} calculateParams={{
                        decimals: 2,
                        rounding: '0.01',
                        roundingPriceType: 'medium'
                    }} className='text-16 text-gray-51' />
                    {renderProjectProgressBar}
                </div>
                <div className='flex flex-col flex-auto bg-gray-F7 mt-10 px-20 pt-5 pb-15'>
                    <div ref={setNodeRef} style={{minHeight: '500px'}}>
                        {renderThis}
                    </div>
                </div>
            </div>
        </SortableContext>
    )
}


interface DraggableProjectProps {
    id: string
    project: CustomerProjectModelType
    index: string
    originalContainer: number
    handleModalEditOpened: (project: CustomerProjectModelType) => void
    totalsProject: TotalsResultBagOfHoursProject
}

const DraggableProject: React.FC<DraggableProjectProps> = ({ id, project, index, originalContainer, handleModalEditOpened, totalsProject }) => {
    const { attributes, listeners, setNodeRef, transform } = useSortable({
        id,
        data: {
            projectId: id,
            index,
            project,
            originalContainer
        },
    });
    const style = {
        transform: CSS.Translate.toString(transform),
    };
    const renderThis: JSX.Element[] = []
    if (project.Employees) {
        for (const employee of project?.Employees) {
            renderThis.push(
                <CustomerProjectRenderImageTooltip id={employee.id} img={employee?.profile_image ?? '/user-png.png'}  name={employee?.fullname_short} tooltipName={'customerProjectEmployee'} />
            )
        }
    }
    if (project.Groups) {
        for (const group of project?.Groups) {
            renderThis.push(
                <CustomerProjectRenderImageTooltip id={group.id} img={group?.photo ?? '/group.png'} name={group.name} tooltipName={'customerProjectGroup'} />
            )
        }
    }

    return (
        <div ref={setNodeRef} onClick={() => handleModalEditOpened(project)} style={style} className='mt-12' {...listeners} {...attributes}>
            <div className='flex flex-col gap-2 rounded-lg bg-white px-17 py-10 customer_project_status_flow_card_content'>
                <span className='text-gray-51'>{project.name}</span>
                <span className='text-gray-51 font-light'>{project?.Customer?.name}</span>
                <div className='flex flex-wrap'>
                    <CustomerBoardRatingStars project={project} />
                    {project?.percentageValue && project.percentageValue > 0 ?
                        <DisplayPrice value={project?.percentageValue|| 0} CurrencyId={getISOCurrencyMain()} with_vat={false} calculateParams={{
                            decimals: 2,
                            rounding: '0.01',
                            roundingPriceType: 'medium'
                        }} />
                        : null
                    }
                </div>
                <div className='flex  items-center flex-wrap gap-x-12 gap-y-5'>
                    {renderThis.length ? 
                        <div className='flex flex-wrap gap-x-2 mt-2'>
                            {renderThis}
                        </div>
                        : null
                    }
                    {
                        project.limit_date ?
                            <div className='flex justify-center items-center gap-5'>
                                <TwinIcon className='text-gray-63' icon={faCalendarDays} />
                                <span className='text-gray-8F text-12'>{displayDate(new Date(project?.limit_date || ''))}</span>
                            </div>
                            :
                            null
                    }
                    {
                        project.CustomerProjectComments?.length ?
                            <div className='flex justify-center mr-15 items-center gap-5'>
                                <TwinIcon className='text-gray-63' icon={faComment} />
                                <span className='text-gray-8F'>{project.CustomerProjectComments?.length}</span>
                            </div>
                            :
                        null
                    }
                    {
                        project.TaskBagsOfHours?.length && totalsProject.timeLimit
                        ? <ProjectBagOfHoursProgressBar totalsTime={totalsProject || {}} />
                        : null
                    }
                </div>
            </div>
        </div>
    )
}

interface CustomerBoardRatingStarsProps {
    project: CustomerProjectModelType
}

const CustomerBoardRatingStars: React.FC<CustomerBoardRatingStarsProps> = ({ project }) => {
    const totalStars = 5
    const renderThis: JSX.Element[] = []
    for (let i = 1; i < totalStars + 1; i++) {
        renderThis.push(
            <div key={i}>
                <TwinIcon icon={i <= (project.rating || 0) ? faStarSolid : faStar} className='text-green-43 mr-3' fontSize={15} />
            </div>
        )
    }
    return (
        <div className='flex mr-5'>
            {renderThis}
        </div>
    )
}

interface ProjectBagOfHoursProgressBarProps {
    totalsTime: TotalsResultBagOfHoursProject
}

const ProjectBagOfHoursProgressBar: React.FC<ProjectBagOfHoursProgressBarProps> = ({ totalsTime }) => {
    const { t } = useTwinTranslation()
    const colorBarClassName = (totalsTime?.remainingTime <= 0) ? 'bg-red-BA' : 'bg-green-43'
    return (
        <div className='flex flex-col items-center mt-5 gap-x-10 gap-y-5 w-full'>
            <div className={'draggable_project_parent_bar w-full rounded-full relative ' + (colorBarClassName || '')}>
                <div className='draggable_project_progress_bar bg-gray-DD max-w-full absolute' style={{ width: totalsTime?.consumedPercentatge?.toFixed(2) + '%' }}></div>
            </div>
            <div className='flex w-full items-center justify-between light12 text-gray-97 gap-x-10'>
                <span className='twin_elipsis'>{t('remainingHours', 'Horas restantes')}:</span>
                <div className='flex items-center justify-end whitespace-nowrap'>
                    <TwinIcon className='mb-2 mr-4' icon={faClock} />
                    <span className={totalsTime?.remainingTime <= 0 ? 'text-red-BA' : ''}>{displayFormatedHourMinsFromSecs(totalsTime?.remainingTime) + '/ '}</span>
                    <span>{displayFormatedHourMinsFromSecs(totalsTime?.timeLimit) + 'h'}</span>
                </div>
            </div>
        </div>
    )
}

const customerProjectTypesDispatch = {
    setCustomerProjectTypes: (payload: CustomerProjectTypePayload) => ({ type: 'CHANGE_CUSTOMER_PROJECT_TYPES', payload }),
    setEmployees: (payload: EmployeesPayload) =>({ type: 'CHANGE_EMPLOYEE', payload })
}

export type ReduxCustomerProjectTypes = ConnectedProps<typeof customerProjectTypesConnect>
const mapCustomerProjectTypesConnector = (state: AllReduxPayloads) => ({ customerProjectTypes: state.customerProjectTypes, employees: state.employees })
const customerProjectTypesConnect = connect(mapCustomerProjectTypesConnector, customerProjectTypesDispatch)

const customerProjectTypesConnectLoading = withLoading(CustomerProjectFlow, [{ fetchUrl: '/api/app/customer/customerProject/customerProjectTypes/getAllCustomerProjectTypesFull', propName: 'customerProjectTypes', setFunctionName: 'setCustomerProjectTypes' }, {fetchUrl: '/api/app/employee/getAllEmployees', propName: 'employees', setFunctionName: 'setEmployees' }])

const CustomerProjectFlowConnect = customerProjectTypesConnect(customerProjectTypesConnectLoading)

export default CustomerProjectFlowConnect